import { BigDecimal } from "@solomon-labs/bigdecimal";
import { RetryIcon, TooltipIcon } from "@solomon-labs/design/components/icons";
import {
    Button,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from "@solomon-labs/design/components/ui";
import React, { useCallback, useEffect, useRef, useState } from "react";

import type { ProductJourney } from "../analytics/productEvents";
import { useErrorReturnReady } from "../hooks/transactions/useErrorReturnReady";
import { useSuccessAutoReturn } from "../hooks/transactions/useSuccessAutoReturn";
import { useTransactionPhase } from "../hooks/transactions/useTransactionPhase";
import {
    useAccessControlOrchestration as useAccessControl,
    useAnalyticsCapture,
    useSimMeasureCardHeight,
    useSimMode,
    useSwapOrchestration,
    useWalletModalOrchestration as useWalletModal
} from "../sim";
import { simForce } from "../sim/forceState";
import { LEGACY_USDV_MINT } from "../tokens/usdv";
import { collapseStyle } from "../utils/collapseStyle";

import { AmountInput } from "./primitives/AmountInput";
import { FeeFooter } from "./primitives/FeeFooter";
import { RateRow } from "./primitives/RateRow";
import { ReceiptBlock } from "./primitives/ReceiptBlock";
import { TokenChip } from "./primitives/TokenChip";
import { TransitionButton } from "./primitives/TransitionButton";
import { WidgetShell } from "./primitives/WidgetShell";
import { useSimCursor } from "./sim-cursor/SimCursor";
import {
    getSwapSuccessLabel,
    getSwapTokenIcon,
    USDC_ICON,
    USDV_ICON
} from "./swap/swapLabels";
import type { TokenSelectorProps } from "./types/tokenSelector";

const LEGACY_USDV_MINTS = [LEGACY_USDV_MINT] as const;

// Receipt-row stagger keyframe — required by ReceiptBlock when staggerDelay is set.
const RECEIPT_KEYFRAMES_CSS = `
@keyframes receipt-row-in {
    0%   { opacity: 0; transform: translateY(-8px); }
    100% { opacity: 1; transform: translateY(0); }
}
@media (prefers-reduced-motion: reduce) {
    @keyframes receipt-row-in { from, to { opacity: 1; transform: none; } }
}
`;

/**
 * @version 2.0.0
 * @published 2026-04-25
 * @history
 *   2.0.0 — Wired the in-place LandingBuyFlow phases. The widget now
 *           progresses through `empty → focused → filled → pending →
 *           processing → success → error` in place: You Send + Rate collapse,
 *           a ReceiptBlock expands inside the bordered area, and the CTA
 *           transitions through `Confirm in Wallet… → Processing… → Bought
 *           X USDv` via `TransitionButton`. Error messages mount inline
 *           inside the card. No Stake CTA is chained — see comment below.
 *   1.2.0 — Rebuilt on `WidgetShell` variant="landing". Added focus border
 *           animations, meta row expansion on focus, and fees that slide in
 *           below the button when the user enters a value.
 *   1.1.0 — Wired the form to real swap state via `useSwap`.
 *   1.0.0 — Initial publish.
 */
interface BuyWidgetProps {
    analytics?: ProductJourney;
    /** Forwarded to the underlying `WidgetShell`. The widget is now
     *  width-agnostic (it fills its parent column), so this is only
     *  needed when a consumer wants to add layout classes to the
     *  shell wrapper beyond width. */
    className?: string;
    /** When true, the RateRow's swap-direction button is interactive
     *  and flips Sell ↔ Buy (e.g. USDC → USDv becomes USDv → USDC).
     *  Default `false` — buy-only flows (disconnected USDv landing,
     *  users with no USDv to sell) render the button as disabled. */
    canFlip?: boolean;
    /** Optional Jupiter-backed token picker. apps/main injects its
     *  production `TokenSelector` here; sim-only consumers omit it
     *  and the picker UI is silently skipped (no user click in
     *  scripted demos). Kept off the widget bundle so external
     *  consumers don't pull `@solomon-labs/swap-client`. */
    TokenSelectorComponent?: React.ComponentType<TokenSelectorProps>;
    /** Runs 3s after a swap confirms, once the widget has reset itself
     *  back to the form. The receipt has nothing left to say by then and
     *  this widget owns no routing, so the consumer decides where the
     *  user lands — refresh balances, close the panel, or both. Omit it
     *  and the widget still clears its own success state. */
    onSuccessReturn?: () => void;
    analyticsEntryPoint?: "landing_buy_widget" | "rewards_buy_panel";
}

export function BuyWidget({
    analytics,
    className,
    canFlip = false,
    TokenSelectorComponent,
    onSuccessReturn,
    analyticsEntryPoint = "landing_buy_widget"
}: BuyWidgetProps = {}): React.ReactElement {
    const swap = useSwapOrchestration({
        entry_point: analyticsEntryPoint,
        analytics
    });
    const { openModal } = useWalletModal();
    const { isAllowed, isChecked } = useAccessControl();
    const legacyCapture = useAnalyticsCapture();
    const capture = swap.analyticsCapture ?? legacyCapture;

    // ── Sim-mode cursor (sim only) ────────────────────────────────────
    // In production (`sim.enabled === false`) this widget must render
    // byte-for-byte as before: no cursor container, no overlay layer, no
    // moves. The `data-sim-cursor-key` anchors on the input/button are
    // inert without a cursor and stay in the production tree.
    const sim = useSimMode();
    const cursor = useSimCursor();
    // Stable move/click/hide refs so the driving effects below don't
    // re-fire on unrelated re-renders.
    const {
        moveTo: moveCursor,
        moveToElement: moveCursorTo,
        click: clickCursor,
        hide: hideCursor
    } = cursor;
    // Ref to the SEND amount's actual <input>. We point the cursor at THIS
    // element (its box hugs the amount text) rather than the field's tall
    // wrapper `<div>` — the wrapper's vertical centre lands well below the
    // digits, and a small offset can't correct that mismatch.
    const sendInputRef = useRef<HTMLInputElement | null>(null);
    // The cursor's positioned container is the `relative` wrapper we add
    // around the widget tree only in sim mode (see the return branch).
    const setCursorContainerRef = useCallback(
        (el: HTMLDivElement | null): void => {
            cursor.containerRef.current = el;
        },
        [cursor.containerRef]
    );

    // `swap_tab_viewed` once per mount — landing buy widget surface.
    useEffect(() => {
        capture("swap_tab_viewed", {
            mode: "buy",
            entry_source: "landing_buy_widget"
        });
    }, [capture]);

    // Fire `gate_blocked` once when the denied state becomes visible.
    const gateBlockedFiredRef = useRef(false);
    useEffect(() => {
        const isDenied = swap.connected && isChecked && !isAllowed;
        if (!isDenied) {
            gateBlockedFiredRef.current = false;
            return;
        }
        if (gateBlockedFiredRef.current) return;
        gateBlockedFiredRef.current = true;
        capture("gate_blocked", {
            surface: "buy_widget",
            reason: "not_whitelisted"
        });
    }, [swap.connected, isChecked, isAllowed, capture]);
    const [sendTokenSelectorOpen, setSendTokenSelectorOpen] = useState(false);
    const [receiveTokenSelectorOpen, setReceiveTokenSelectorOpen] =
        useState(false);
    const [isInputting, setIsInputting] = useState(false);
    // Capture the filled card on submit as a minimum height. The receipt can
    // grow when its content or responsive typography needs more room.
    const cardRef = useRef<HTMLDivElement>(null);

    const handleFocus = (): void => setIsInputting(true);
    const handleBlur = (): void => {
        setIsInputting(false);
        swap.handleBlur();
    };

    const hasInput = new BigDecimal(swap.sendAmount || "0").gt(0);

    // Map useSwap state into the in-place phase machine. `isFocused` only
    // promotes to `"focused"` when the user has clicked into the input WITHOUT
    // a value — see useTransactionPhase. LandingBuyFlow is the only consumer
    // that uses this distinct phase to keep the meta row (balance / dollar
    // conversion) collapsed until engagement.
    const phase = useTransactionPhase({
        isPending: swap.swapView === "pending",
        isSuccess: swap.swapView === "success",
        executeState: swap.executeState,
        hasValue: hasInput,
        // `swap.simIsFocused` is set by the sim orchestrator during
        // the `"focused"` phase; production leaves it undefined and
        // the widget falls back to its real DOM-focus state.
        isFocused: isInputting || swap.simIsFocused === true
    });

    const isPendingOrLater =
        phase === "pending" ||
        phase === "processing" ||
        phase === "success" ||
        phase === "error";
    // Receipt is visible from `pending` onward (including the
    // "Confirm in Wallet..." window) so the user can see what they're
    // signing — matches `SwapContent`'s in-place flow on the connected
    // /-with-balance widget. Previously the receipt was gated behind
    // `phase !== "pending"`, leaving the pending screen with just the
    // headline amount + Confirm button (no Price / Fee / Sending breakdown).
    const isReceiptOrLater = isPendingOrLater;
    const isProcessing = phase === "processing";
    const isSuccess = phase === "success";
    const isError = phase === "error";
    const errorReturnReady = useErrorReturnReady(isError);
    // Success is a receipt, not a destination: hold it 3s, then reset the
    // form and hand routing back to the consumer. Skipped in sim mode,
    // where scripted demos park on the receipt deliberately.
    const handleSuccessReturn = useCallback((): void => {
        swap.handleSuccessClose();
        onSuccessReturn?.();
    }, [swap, onSuccessReturn]);
    useSuccessAutoReturn(isSuccess && !sim.enabled, handleSuccessReturn);
    // The meta row (dollar value + balance) shows as soon as we've left the
    // empty phase — focus, fill, or any later phase reveals it.
    const showMeta = phase !== "empty";

    // ── Cursor choreography (sim only) ────────────────────────────────
    // Driven state-reactively off the sim's own observable phase, since
    // the timeline lives in the orchestrator (useSwapOrchestration), not
    // here. The interaction VISUALS (focus, typing, CTA hover/press) are
    // already driven by `swap.simIsFocused` / `swap.sendAmount` /
    // `swap.demoSubmitState`; the cursor just points at where each beat
    // is happening. No effect does anything in production (`!sim.enabled`).
    //
    // Beat 1 — the input is engaged (empty→focused, before typing starts):
    //   travel to the amount input. `align:'left'` points a bit inset from
    //   the left edge, natural for a wide field; glyph auto-detects to the
    //   text I-beam. This lands ~before the `filled` typing beat plays.
    const simFocused = swap.simIsFocused === true;
    useEffect(() => {
        if (!sim.enabled) return;
        // `cursor: "text"` is forced because the anchor is the field's
        // wrapper <div> (AmountInput doesn't forward data-* to its inner
        // <input>), so auto-detect would pick the arrow instead of the
        // text I-beam that a numeric input warrants.
        if (simFocused) {
            // Point at the actual <input> (its box hugs the amount line) so
            // the I-beam lands ON the digits, not the wrapper's centre.
            moveCursorTo(sendInputRef.current, {
                align: "left",
                cursor: "text"
            });
        }
    }, [sim.enabled, simFocused, moveCursorTo]);

    // Beat 2/3 — the scripted CTA hover→press. On hover, hop to the submit
    // button (pointer glyph auto-detected); on press, pulse the click ring.
    // `swap.demoSubmitState` is the orchestrator's own hover/press signal —
    // the same one that drives the Button's `forceHover`/`forcePressed`.
    const demoSubmitState = swap.demoSubmitState;
    useEffect(() => {
        if (!sim.enabled) return;
        if (demoSubmitState === "hover") moveCursor("submit");
        else if (demoSubmitState === "press") clickCursor();
    }, [sim.enabled, demoSubmitState, moveCursor, clickCursor]);

    // Beat 4 — once we're pending or later (confirm / processing / success
    // / error), the interaction is done; fade the cursor out so it doesn't
    // hover over the collapsing form + receipt.
    useEffect(() => {
        if (!sim.enabled) return;
        if (isPendingOrLater) hideCursor();
    }, [sim.enabled, isPendingOrLater, hideCursor]);

    // Buy mode: Sell USDC → Buy USDv. Sell mode (after the RateRow flip):
    // Sell USDv → Buy USDC. Symbols + icons resolve from the live swap
    // options, falling back to the mode-appropriate pair so the chips never
    // render the wrong token while the option list loads.
    const isBuy = swap.mode === "buy";
    const sendSymbol =
        swap.sendDisplayOption?.label ??
        swap.sendCurrencySymbol ??
        (isBuy ? "USDC" : "USDv");
    const receiveSymbol =
        swap.receiveDisplayOption?.label ??
        swap.receiveCurrencySymbol ??
        (isBuy ? "USDv" : "USDC");
    const sendIcon = getSwapTokenIcon(
        sendSymbol,
        swap.sendDisplayOption?.icon ?? (isBuy ? USDC_ICON : USDV_ICON)
    );
    const receiveIcon = getSwapTokenIcon(
        receiveSymbol,
        swap.receiveDisplayOption?.icon ?? (isBuy ? USDV_ICON : USDC_ICON)
    );
    const rateLabel =
        swap.exchangeRate !== null
            ? `1 ${sendSymbol} = ${swap.exchangeRate} ${receiveSymbol}`
            : `1 ${sendSymbol} = - ${receiveSymbol}`;

    // Snapshot drives all values once we're at pending or later — the form
    // amounts get cleared on success, so we read from the captured snapshot.
    const snapshot = swap.swapSnapshot;
    // The receipt mounts once the snapshot exists, partway into `pending`
    // (its data arrives with the quote the wallet signs). It renders
    // collapsed for one frame and expands on the next, so collapseStyle has
    // a start to animate from; its rows stagger in once, on mount. Mirrors
    // SwapContent's `receiptExpanded`.
    const receiptMounted = isReceiptOrLater && !!snapshot;
    const [receiptExpanded, setReceiptExpanded] = useState(false);
    useEffect(() => {
        if (!receiptMounted) {
            setReceiptExpanded(false);
            return;
        }
        const frame = requestAnimationFrame(() => setReceiptExpanded(true));
        return (): void => cancelAnimationFrame(frame);
    }, [receiptMounted]);
    const displaySendAmount =
        isPendingOrLater && snapshot ? snapshot.sendAmount : swap.sendAmount;
    const displayReceiveAmount =
        isPendingOrLater && snapshot
            ? snapshot.receiveAmount
            : swap.receiveAmountDisplay || "";
    const displaySendSymbol =
        isPendingOrLater && snapshot
            ? snapshot.sendSymbol || sendSymbol
            : sendSymbol;
    const displayReceiveSymbol =
        isPendingOrLater && snapshot
            ? snapshot.receiveSymbol || receiveSymbol
            : receiveSymbol;
    const displaySendIcon =
        isPendingOrLater && snapshot?.sendIcon ? snapshot.sendIcon : sendIcon;
    const displayReceiveIcon =
        isPendingOrLater && snapshot?.receiveIcon
            ? snapshot.receiveIcon
            : receiveIcon;

    // Receipt rows — sourced from the snapshot so values stay frozen across
    // pending/processing/success transitions.
    // "Price" is the counter-currency (USDC) leg: what the user *paid* in Buy
    // mode (send leg) and what they *received* in Sell mode (receive leg). It
    // always complements the USDv headline in the summary below rather than
    // echoing it.
    const receiptRows = snapshot
        ? [
              {
                  label: "Price",
                  value: isBuy
                      ? `${snapshot.sendAmount} ${snapshot.sendSymbol}`
                      : `${snapshot.receiveAmount} ${snapshot.receiveSymbol}`
              },
              {
                  label: "Price Impact",
                  value: snapshot.priceImpactDisplay
              },
              { label: "Fee", value: snapshot.routingFeeDisplay },
              { label: "Network Cost", value: snapshot.networkFeeDisplay }
          ]
        : [];

    // Receipt summary is the USDv-centric headline. The USDv leg is the
    // *receive* side in Buy mode and the *send* side in Sell mode, so the line
    // reads "Bought X USDv" / "Sold X USDv" — never "Sold X USDC".
    const receiptSummaryLabel = isError
        ? isBuy
            ? "Did not buy"
            : "Did not sell"
        : isSuccess
          ? isBuy
              ? "Bought"
              : "Sold"
          : isBuy
            ? "Buying..."
            : "Selling...";
    const receiptSummaryValue = snapshot
        ? isBuy
            ? `${snapshot.receiveAmount} ${snapshot.receiveSymbol}`
            : `${snapshot.sendAmount} ${snapshot.sendSymbol}`
        : "";

    // Labels reflect phase progression — mirrors LandingBuyFlow showcase.
    // Form-view labels read as the action ("Sell" / "Buy") so the
    // landing-page widget matches the SwapContent treatment.
    const sendLabel = isPendingOrLater ? "Sending..." : "Sell";
    const getLabel = isError
        ? isBuy
            ? "Did not buy"
            : "Did not sell"
        : isSuccess
          ? isBuy
              ? "Bought"
              : "Sold"
          : isPendingOrLater
            ? isBuy
                ? "Buying..."
                : "Selling..."
            : "Buy";
    const getMode: "compact" | "display" = isPendingOrLater
        ? "compact"
        : "display";

    // The send-token picker only applies to the token you pay *with* — i.e.
    // Buy mode (Sell USDC → Buy USDv). In Sell mode the send leg IS the USDv
    // you're selling, so there's nothing to pick: no chevron, no click.
    // Mirrors SwapContent's `sendHasChev = isBuy`.
    const sendSelectable = isBuy && !isPendingOrLater;
    const receiveSelectable =
        !isBuy && !isPendingOrLater && !!TokenSelectorComponent;

    // Quote failure is the one feedback state with a way out. The query
    // has already retried twice and will refetch on its 15s interval, so
    // the label flashes the failure first and only then offers the manual
    // retry — long enough to read what happened, short enough that the
    // 15s wait is not the only option.
    const quoteFailed = swap.quoteError !== null && !!swap.sendAmount;
    const QUOTE_RETRY_DELAY_MS = 2000;
    const [showQuoteRetry, setShowQuoteRetry] = useState(false);
    useEffect(() => {
        if (!quoteFailed) {
            setShowQuoteRetry(false);
            return;
        }
        setShowQuoteRetry(false);
        const timer = window.setTimeout(
            () => setShowQuoteRetry(true),
            QUOTE_RETRY_DELAY_MS
        );
        return (): void => window.clearTimeout(timer);
    }, [quoteFailed]);

    const submitDisabled =
        (swap.isErrorFeedback && !showQuoteRetry) ||
        !hasInput ||
        !swap.canExecuteQuote ||
        swap.isQuoteLoading ||
        swap.executeState.isLoading;

    // Success-state label — "Bought X USDv" in buy mode, "Sold X USDC" in
    // sell mode. Reuses the connected widget's tested policy so the two
    // surfaces never drift.
    const successLabel = getSwapSuccessLabel({ mode: swap.mode, snapshot });

    /*
     * No "Stake USDv" CTA is chained after success here:
     * - The reference LandingBuyFlow in TradeWidgetShowcase ends at success
     *   (no postSuccess phase). The Stake CTA chain belongs to the in-app
     *   BuyFlow, which lives in a different shell with its own stake mode.
     * - This BuyWidget renders only when the user is disconnected. Once the
     *   buy completes the wallet is connected, USDvPage swaps to the
     *   connected layout, and the widget unmounts — so a stake CTA here
     *   would have nowhere to navigate to without leaving the disconnected
     *   surface.
     */

    const button = !swap.connected ? (
        <Button
            variant="raised"
            size="lg"
            interaction="light-bar"
            fullWidth
            onClick={(): void => {
                openModal("buy_widget");
            }}
        >
            Connect Wallet
        </Button>
    ) : isChecked && !isAllowed ? (
        <Button
            variant="raised"
            size="lg"
            interaction="light-bar"
            fullWidth
            disabled
        >
            Not Whitelisted
        </Button>
    ) : isSuccess ? (
        // No follow-up CTA by design (no Stake chain — see the comment
        // block above), and none is needed: `useSuccessAutoReturn` clears
        // this receipt after 3s. TransitionButton is reused for the
        // success-state styling and to keep parity with the showcase.
        <TransitionButton
            showCta={false}
            successLabel={successLabel}
            ctaLabel={successLabel}
        />
    ) : isError ? (
        // 2s after entering error, "Buy Failed" → "Return to Buy" via
        // fade-shrink-blur. Click runs `swap.handlePendingCancel()`
        // which preserves sendAmount (only resets executeState +
        // snapshot + view back to "form").
        <TransitionButton
            showCta={errorReturnReady}
            successLabel={isBuy ? "Buy Failed" : "Sell Failed"}
            ctaLabel={isBuy ? "Return to Buy" : "Return to Sell"}
            initialStatus="error"
            transition="fade-shrink-blur"
            onClick={() => swap.handlePendingCancel()}
        />
    ) : (
        <Button
            variant="raised"
            size="lg"
            interaction="light-bar"
            fullWidth
            data-sim-cursor-key="submit"
            disabled={submitDisabled}
            // Every error-severity feedback gets the error rail, not just a
            // failed transaction: insufficient balance, a rejected
            // keystroke and a dead quote are all failures and should read
            // alike. Warnings ("Proceed?") deliberately do not — Button has
            // no warning status, and they are not failures. The retry
            // affordance does not either: it is the way out, not the fault.
            status={
                isError ||
                (swap.ctaFeedback?.severity === "error" && !showQuoteRetry)
                    ? "error"
                    : isPendingOrLater
                      ? "pending"
                      : undefined
            }
            {...simForce(swap.demoSubmitState)}
            onClick={() =>
                showQuoteRetry
                    ? (capture("swap_quote_retry_clicked"), swap.refetchQuote())
                    : void swap.handleSubmit(cardRef.current?.offsetHeight)
            }
        >
            {showQuoteRetry ? (
                // Same shape as the enrollment retry: glyph, gap-3, label.
                <span className="flex items-center justify-center gap-3">
                    <RetryIcon className="size-5" />
                    Retry Quote Fetch
                </span>
            ) : isError ? (
                isBuy ? (
                    "Buy Failed"
                ) : (
                    "Sell Failed"
                )
            ) : isProcessing ? (
                "Processing..."
            ) : isPendingOrLater ? (
                "Confirm in Wallet..."
            ) : swap.executeState.isLoading ? (
                isBuy ? (
                    "Buying…"
                ) : (
                    "Selling…"
                )
            ) : swap.ctaFeedback?.tooltip ? (
                [
                    swap.ctaFeedback.message,
                    <TooltipProvider key="cta-tip">
                        <Tooltip>
                            {/* A span, not a button: this sits
                                          INSIDE the CTA, and a nested
                                          button is invalid and steals the
                                          click. */}
                            <TooltipTrigger
                                render={
                                    <span
                                        aria-label="What does this warning mean?"
                                        className="ml-1 inline-flex cursor-help items-center"
                                    />
                                }
                            >
                                <TooltipIcon className="size-4" />
                            </TooltipTrigger>
                            <TooltipContent
                                side="top"
                                className="text-balance max-w-56"
                            >
                                {swap.ctaFeedback.tooltip}
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                ]
            ) : (
                (swap.ctaFeedback?.message ??
                (isBuy ? "Buy USDv" : "Sell USDv"))
            )}
        </Button>
    );

    // Keep the filled form's height as a floor, not a clipping boundary.
    // A receipt can be taller than the captured form (different host fonts,
    // a breakpoint change, or an error). A fixed height hides bottom padding
    // and can crop the action. Let the shell grow to fit its content.
    // Sims measure automatically; production captures the height on submit.
    const simCardHeight = useSimMeasureCardHeight(cardRef, hasInput);
    const effectiveCardHeight = simCardHeight ?? swap.cardHeight;
    const cardStyle: React.CSSProperties | undefined =
        isPendingOrLater && effectiveCardHeight
            ? { minHeight: effectiveCardHeight }
            : undefined;

    const content = (
        <>
            <style
                dangerouslySetInnerHTML={{ __html: RECEIPT_KEYFRAMES_CSS }}
            />
            <WidgetShell
                button={button}
                cardRef={cardRef}
                cardStyle={cardStyle}
                className={className}
                footerExpanded={hasInput && !isPendingOrLater}
                footer={
                    <FeeFooter
                        priceImpact={swap.priceImpactDisplay}
                        fee={swap.routingFeeDisplay}
                        networkCost={swap.networkFeeDisplay}
                        loading={swap.feeFooterLoading && hasInput}
                    />
                }
            >
                {/* Single child — bypasses WidgetShell's flex gap so collapsing
                    Send + Rate doesn't leave ghost gaps between rows. */}
                <div className="flex flex-col">
                    {/* Send field — collapses in pending+. `mb-3
                        md:mb-5` owns the expanded margin (12px
                        mobile / 20px desktop); collapseStyle's
                        `marginBottomFromClass` lets the class win
                        when expanded and just animates to 0 inline
                        when collapsed. */}
                    <div
                        className="mb-5"
                        style={collapseStyle(isPendingOrLater, {
                            maxHeight: 200,
                            marginBottomFromClass: true,
                            duration: 600,
                            opacityDuration: 400
                        })}
                    >
                        <AmountInput
                            label={sendLabel}
                            value={displaySendAmount}
                            inputRef={sendInputRef}
                            onChange={(v) => swap.handleSendAmountChange(v)}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            placeholder="0.00"
                            focusBorder
                            fit
                            maxDecimals={swap.sendTokenDecimals}
                            rejectionTrigger={swap.rejectionCount}
                            secondary={
                                isPendingOrLater && snapshot
                                    ? `$${snapshot.sendUsdValue || "0.00"}`
                                    : `$${swap.usdValue}`
                            }
                            balance={swap.sendDisplayBalance}
                            onBalanceClick={
                                isPendingOrLater
                                    ? undefined
                                    : swap.handleMaxClick
                            }
                            metaCollapsed={!showMeta}
                            tokenChip={
                                <div
                                    className={
                                        sendSelectable ? "cursor-pointer" : ""
                                    }
                                    onClick={
                                        sendSelectable
                                            ? (): void => {
                                                  capture(
                                                      "swap_token_picker_opened",
                                                      {
                                                          mode: "buy",
                                                          field: "send",
                                                          current_token_symbol:
                                                              displaySendSymbol
                                                      }
                                                  );
                                                  setSendTokenSelectorOpen(
                                                      true
                                                  );
                                              }
                                            : undefined
                                    }
                                >
                                    <TokenChip
                                        icon={displaySendIcon}
                                        symbol={displaySendSymbol}
                                        rounded
                                        selectable={sendSelectable}
                                        framed={false}
                                    />
                                </div>
                            }
                        />
                    </div>

                    {/* Conversion rate bar — collapses in pending+.
                        Same `mb-5` + `marginBottomFromClass` pattern
                        as the send field above. */}
                    <div
                        className="mb-5"
                        style={collapseStyle(isPendingOrLater, {
                            maxHeight: 50,
                            marginBottomFromClass: true,
                            duration: 400,
                            opacityDuration: 200
                        })}
                    >
                        <RateRow
                            rate={rateLabel}
                            onToggle={
                                canFlip ? swap.handleSwapClick : undefined
                            }
                        />
                    </div>

                    {/* Receive field — read-only display */}
                    <div className="flex flex-col">
                        <AmountInput
                            label={getLabel}
                            value={displayReceiveAmount}
                            placeholder="0.00"
                            focusBorder
                            fit
                            secondary={
                                isPendingOrLater && snapshot
                                    ? `$${snapshot.receiveUsdValue || "0.00"}`
                                    : `$${swap.receiveUsdValue}`
                            }
                            balance={swap.receiveDisplayBalance}
                            metaCollapsed={!showMeta || isPendingOrLater}
                            tokenChip={
                                <button
                                    type="button"
                                    aria-label="Select buy token"
                                    disabled={!receiveSelectable}
                                    onClick={() => {
                                        capture("swap_token_picker_opened", {
                                            mode: swap.mode,
                                            field: "receive",
                                            current_token_symbol:
                                                displayReceiveSymbol
                                        });
                                        setReceiveTokenSelectorOpen(true);
                                    }}
                                >
                                    <TokenChip
                                        icon={displayReceiveIcon}
                                        symbol={displayReceiveSymbol}
                                        selectable={receiveSelectable}
                                        framed={false}
                                    />
                                </button>
                            }
                            mode={getMode}
                            loading={
                                swap.isQuoteFetching &&
                                hasInput &&
                                !isPendingOrLater
                            }
                        />

                        {/* Receipt — grows in once the snapshot arrives, stays through success */}
                        {receiptMounted && (
                            <div
                                style={collapseStyle(!receiptExpanded, {
                                    maxHeight: 300,
                                    marginTop: 16,
                                    duration: 500,
                                    opacityDuration: 400
                                })}
                            >
                                <ReceiptBlock
                                    rows={receiptRows}
                                    summaryLabel={receiptSummaryLabel}
                                    summaryValue={receiptSummaryValue}
                                    // Constant, so the rows stagger once on
                                    // mount and never replay on a phase change.
                                    staggerDelay={80}
                                />
                            </div>
                        )}
                    </div>

                    {/* Inline error message — mirrors showcase. Reserves a
                        non-breaking space when not erroring so the card height
                        doesn't jump. */}
                    <p
                        className="font-sans text-sm text-error-base"
                        style={collapseStyle(!isError, {
                            marginTop: 16,
                            duration: 300
                        })}
                    >
                        {isError
                            ? (swap.executeState.errorMessage ??
                              "Transaction failed. Please try again.")
                            : "\u00A0"}
                    </p>
                </div>
            </WidgetShell>

            {TokenSelectorComponent && (
                <TokenSelectorComponent
                    options={swap.receiveAssetOptions}
                    excludedMints={LEGACY_USDV_MINTS}
                    value={swap.receiveCurrency}
                    onChange={(value) =>
                        swap.handleCurrencyChange("receive", value)
                    }
                    isOpen={receiveTokenSelectorOpen && receiveSelectable}
                    onToggle={() => (
                        capture("swap_token_picker_closed", {
                            field: "receive"
                        }),
                        setReceiveTokenSelectorOpen(!receiveTokenSelectorOpen)
                    )}
                    onSelectOption={({ option }) =>
                        swap.setReceiveSelectedOption(option)
                    }
                />
            )}

            {TokenSelectorComponent && (
                <TokenSelectorComponent
                    options={swap.sendAssetOptions}
                    excludedMints={LEGACY_USDV_MINTS}
                    value={swap.sendCurrency}
                    onChange={(value) =>
                        swap.handleCurrencyChange("send", value)
                    }
                    isOpen={sendTokenSelectorOpen}
                    onToggle={() => (
                        capture("swap_token_picker_closed", { field: "send" }),
                        setSendTokenSelectorOpen(!sendTokenSelectorOpen)
                    )}
                    onSelectOption={({ option }) => {
                        swap.setSendSelectedOption(option);
                    }}
                />
            )}
        </>
    );

    // Production (`!sim.enabled`): render the widget tree exactly as
    // before — no wrapper, no cursor overlay, zero visual diff. Only in
    // sim mode do we wrap in a positioned container and mount the cursor
    // layer, which travels to the `data-sim-cursor-key` anchors above.
    if (!sim.enabled) return content;

    return (
        <div ref={setCursorContainerRef} className="relative">
            {content}
            {cursor.layer}
        </div>
    );
}
